import { useState, useEffect } from 'react';
import { getAnalyticsEvents, clearAnalyticsEvents } from '../../utils/analytics';

export default function AdminDashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [events, setEvents] = useState(getAnalyticsEvents());

  const ADMIN_PASSWORD = 'epicjuice';

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setError('');
    } else {
      setError('Incorrect password');
      setPassword('');
    }
  };

  const handleClearData = () => {
    if (window.confirm('Are you sure you want to clear all analytics data?')) {
      clearAnalyticsEvents();
      setEvents([]);
    }
  };

  const handleRefresh = () => {
    setEvents(getAnalyticsEvents());
  };

  useEffect(() => {
    if (isAuthenticated) {
      const interval = setInterval(() => {
        setEvents(getAnalyticsEvents());
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const pageViews = events.filter(e => e.event === 'page_view').length;
  const calendarClicks = events.filter(e => e.event === 'calendar_click').length;
  const clickThroughRate = pageViews > 0 ? ((calendarClicks / pageViews) * 100).toFixed(2) : '0.00';

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="bg-zinc-900 rounded-lg p-8 border border-zinc-800">
            <div className="flex items-center justify-center mb-6">
              <i className="ri-shield-keyhole-line text-4xl text-red-600"></i>
            </div>
            <h1 className="text-2xl font-bold text-white text-center mb-2">Admin Access</h1>
            <p className="text-zinc-400 text-center mb-6 text-sm">Enter password to view analytics</p>
            
            <form onSubmit={handleLogin}>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-red-600 mb-4"
                autoFocus
              />
              {error && (
                <p className="text-red-500 text-sm mb-4">{error}</p>
              )}
              <button
                type="submit"
                className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-lg transition-colors whitespace-nowrap"
              >
                Access Dashboard
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <i className="ri-dashboard-line text-3xl text-red-600"></i>
            <h1 className="text-3xl font-bold">Analytics Dashboard</h1>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleRefresh}
              className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              Refresh
            </button>
            <button
              onClick={handleClearData}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              <i className="ri-delete-bin-line"></i>
              Clear Data
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-zinc-900 rounded-lg p-6 border border-zinc-800">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-blue-600/20 rounded-lg flex items-center justify-center">
                <i className="ri-eye-line text-xl text-blue-500"></i>
              </div>
              <h3 className="text-zinc-400 text-sm font-medium">Total Page Views</h3>
            </div>
            <p className="text-4xl font-bold">{pageViews}</p>
          </div>

          <div className="bg-zinc-900 rounded-lg p-6 border border-zinc-800">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-red-600/20 rounded-lg flex items-center justify-center">
                <i className="ri-calendar-check-line text-xl text-red-500"></i>
              </div>
              <h3 className="text-zinc-400 text-sm font-medium">Calendar Clicks</h3>
            </div>
            <p className="text-4xl font-bold">{calendarClicks}</p>
          </div>

          <div className="bg-zinc-900 rounded-lg p-6 border border-zinc-800">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-green-600/20 rounded-lg flex items-center justify-center">
                <i className="ri-percent-line text-xl text-green-500"></i>
              </div>
              <h3 className="text-zinc-400 text-sm font-medium">Click-Through Rate</h3>
            </div>
            <p className="text-4xl font-bold">{clickThroughRate}%</p>
          </div>
        </div>

        <div className="bg-zinc-900 rounded-lg border border-zinc-800 overflow-hidden">
          <div className="p-6 border-b border-zinc-800">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <i className="ri-list-check text-red-600"></i>
              Event Log
            </h2>
            <p className="text-zinc-400 text-sm mt-1">
              {events.length} total events recorded
            </p>
          </div>
          
          <div className="overflow-x-auto">
            {events.length === 0 ? (
              <div className="p-12 text-center">
                <i className="ri-inbox-line text-5xl text-zinc-700 mb-4"></i>
                <p className="text-zinc-500">No analytics data yet</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-zinc-800/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">Event</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">Timestamp</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">Referrer</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800">
                  {[...events].reverse().map((event, index) => (
                    <tr key={index} className="hover:bg-zinc-800/30 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {event.event === 'page_view' ? (
                            <i className="ri-eye-line text-blue-500"></i>
                          ) : (
                            <i className="ri-calendar-check-line text-red-500"></i>
                          )}
                          <span className="font-medium">
                            {event.event === 'page_view' ? 'Page View' : 'Calendar Click'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-zinc-400 text-sm">
                        {new Date(event.timestamp).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-zinc-400 text-sm">
                        {event.referrer === 'direct' ? (
                          <span className="text-zinc-500 italic">Direct</span>
                        ) : (
                          <span className="truncate max-w-xs block">{event.referrer}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}